import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Alert, CircularProgress, Grid, Divider,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { staffService } from '@smartgrow/shared';
import type { Staff } from '@smartgrow/shared/types';

type Role = 'ADMIN' | 'SUPERVISOR' | 'WORKER';

export const EditStaffPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: member, isLoading } = useQuery({
    queryKey: ['staff', id],
    queryFn: () => staffService.getStaffById(id!),
    enabled: !!id,
  });

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'SUPERVISOR' as Role,
    notes: '',
  });

  useEffect(() => {
    if (member) {
      setForm({
        fullName: member.fullName,
        email: member.email,
        phone: (member as any).phone ?? '',
        role: member.role as Role,
        notes: (member as any).notes ?? '',
      });
    }
  }, [member]);

  const mutation = useMutation({
    mutationFn: (data: typeof form) =>
      staffService.updateStaff(id!, {
        username: member?.username,
        fullName: data.fullName,
        email: data.email,
        role: data.role as any,
        active: member?.active,
        phone: data.phone || undefined,
        notes: data.notes || undefined,
      } as Partial<Staff>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] });
      navigate('/dashboard/staff');
    },
  });

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.fullName.trim()) e.fullName = 'Full name is required';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field: string, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined as any }));
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (!member) {
    return <Alert severity="error">Staff member not found.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="large">Back</Button>
        <Box>
          <Typography variant="h4" fontWeight="bold">Edit Staff Member</Typography>
          <Typography variant="body2" color="text.secondary">@{member.username}</Typography>
        </Box>
      </Box>

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>Failed to update staff member. Please try again.</Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>Account Details</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField label="Full Name *" value={form.fullName} onChange={(e) => set('fullName', e.target.value)} fullWidth error={!!errors.fullName} helperText={errors.fullName} size="medium" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Username" value={member.username} fullWidth disabled helperText="Username cannot be changed" size="medium" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Email *" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} fullWidth error={!!errors.email} helperText={errors.email} size="medium" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Phone (optional)" value={form.phone} onChange={(e) => set('phone', e.target.value)} fullWidth size="medium" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select label="Role"
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              fullWidth size="medium"
            >
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="SUPERVISOR">Supervisor</MenuItem>
              <MenuItem value="WORKER">Worker</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Notes (optional)" multiline rows={3}
              value={form.notes} onChange={(e) => set('notes', e.target.value)}
              fullWidth inputProps={{ maxLength: 500 }} size="medium"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button size="large" onClick={() => navigate('/dashboard/staff')} disabled={mutation.isPending}>Cancel</Button>
          <Button
            size="large" variant="contained"
            onClick={() => validate() && mutation.mutate(form)}
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={20} /> : null}
            sx={{ minWidth: 160 }}
          >
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
